// A small read-only syntax highlighter for connector files: TypeScript /
// JavaScript, YAML, JSON and Markdown. Returns the text split into lines of
// classed tokens; anything else comes back as plain lines. No React here.

export type TokenKind = "comment" | "string" | "keyword" | "number" | "type" | "fn" | "key" | "literal" | "heading";
export type Token = { text: string; kind?: TokenKind };
export type Lang = "ts" | "yaml" | "json" | "md" | "plain";

export function langOf(path: string): Lang {
  const ext = path.toLowerCase().split(".").pop() ?? "";
  if (["ts", "tsx", "js", "jsx", "mjs", "cjs", "mts", "cts"].includes(ext)) return "ts";
  if (ext === "yaml" || ext === "yml") return "yaml";
  if (ext === "json") return "json";
  if (ext === "md" || ext === "markdown") return "md";
  return "plain";
}

const TS_KEYWORDS = new Set(
  (
    "as async await break case catch class const continue debugger default delete do else enum export extends " +
    "false finally for from function if implements import in instanceof interface let new null of private " +
    "protected public readonly return satisfies static super switch this throw true try type typeof undefined " +
    "var void while yield keyof never unknown any string number boolean bigint symbol object"
  ).split(" "),
);
const TS_LITERALS = new Set(["true", "false", "null", "undefined", "this"]);

// A regex literal only where an expression starts (after an operator, an
// opening bracket, `=>` or `return`), so `a / b / c` stays division and a JSX
// closing tag `</p>` isn't one.
const TS_RE =
  /(\/\/[^\n]*)|(\/\*[\s\S]*?(?:\*\/|$))|(`(?:[^`\\]|\\[\s\S])*`?)|("(?:[^"\\\n]|\\.)*"?|'(?:[^'\\\n]|\\.)*'?|(?<=(?:^|[(,=:[!&|?{};+\-*%~^]|=>|\breturn)\s*)\/(?![/*])(?:[^/\\\n[]|\\.|\[(?:[^\]\\\n]|\\.)*\])+\/[dgimsuyv]*)|(\b0[xX][\da-fA-F_]+n?\b|\b\d[\d_]*(?:\.\d+)?(?:[eE][+-]?\d+)?n?\b)|([A-Za-z_$][\w$]*)/g;

function tsTokens(src: string): Token[] {
  const out: Token[] = [];
  let last = 0;
  for (const m of src.matchAll(TS_RE)) {
    const i = m.index ?? 0;
    if (i > last) out.push({ text: src.slice(last, i) });
    const [text, line, block, tpl, str, num, ident] = m;
    if (line || block) out.push({ text, kind: "comment" });
    else if (tpl || str) out.push({ text, kind: "string" });
    else if (num) out.push({ text, kind: "number" });
    else if (ident) {
      const next = src.slice(i + text.length).match(/^\s*(.)/)?.[1];
      const prev = src.slice(Math.max(0, i - 1), i);
      if (TS_LITERALS.has(ident)) out.push({ text, kind: "literal" });
      else if (TS_KEYWORDS.has(ident) && prev !== ".") out.push({ text, kind: "keyword" });
      else if (next === "(") out.push({ text, kind: "fn" });
      else if (/^[A-Z]/.test(ident)) out.push({ text, kind: "type" });
      else out.push({ text });
    }
    last = i + text.length;
  }
  if (last < src.length) out.push({ text: src.slice(last) });
  return out;
}

const YAML_RE =
  /((?:^|(?<=\s))#[^\n]*)|("(?:[^"\\\n]|\\.)*"?|'[^'\n]*'?)|((?<=^[ \t]*(?:-[ \t]+)?|[{,[][ \t]*)[\w.?-]+(?=[ \t]*:(?:\s|$)))|((?<=(?::|^[ \t]*-|[{,[])[ \t]*)(?:true|false|null|yes|no)(?=[ \t]*(?:[,\]}#]|$)))|((?<![\w.-])-?\d+(?:\.\d+)?(?![\w.-]))/gm;

function yamlTokens(src: string): Token[] {
  return byRegex(src, YAML_RE, ["comment", "string", "key", "literal", "number"]);
}

const JSON_RE = /("(?:[^"\\\n]|\\.)*")(\s*:)?|\b(true|false|null)\b|(-?\b\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b)/g;

function jsonTokens(src: string): Token[] {
  const out: Token[] = [];
  let last = 0;
  for (const m of src.matchAll(JSON_RE)) {
    const i = m.index ?? 0;
    if (i > last) out.push({ text: src.slice(last, i) });
    const [text, str, colon, lit, num] = m;
    if (str) {
      out.push({ text: str, kind: colon ? "key" : "string" });
      if (colon) out.push({ text: colon });
    } else if (lit) out.push({ text, kind: "literal" });
    else if (num) out.push({ text, kind: "number" });
    last = i + text.length;
  }
  if (last < src.length) out.push({ text: src.slice(last) });
  return out;
}

const MD_RE = /(^```[^\n]*$)|(^#{1,6}[ \t][^\n]*$)|(`[^`\n]+`)|(\*\*[^*\n]+\*\*)/gm;

function mdTokens(src: string): Token[] {
  return byRegex(src, MD_RE, ["comment", "heading", "string", "keyword"]);
}

// One capture group per kind; the first group that matched names the token.
function byRegex(src: string, re: RegExp, kinds: TokenKind[]): Token[] {
  const out: Token[] = [];
  let last = 0;
  for (const m of src.matchAll(re)) {
    const i = m.index ?? 0;
    if (m[0].length === 0) continue;
    if (i > last) out.push({ text: src.slice(last, i) });
    const g = kinds.findIndex((_, k) => m[k + 1] !== undefined);
    out.push({ text: m[0], kind: g === -1 ? undefined : kinds[g] });
    last = i + m[0].length;
  }
  if (last < src.length) out.push({ text: src.slice(last) });
  return out;
}

// The file as lines of tokens; a token spanning lines (block comment,
// template string) is split at each newline and keeps its kind.
export function highlightLines(src: string, lang: Lang): Token[][] {
  const text = src.replace(/\r\n?/g, "\n");
  const tokens =
    lang === "ts"
      ? tsTokens(text)
      : lang === "yaml"
        ? yamlTokens(text)
        : lang === "json"
          ? jsonTokens(text)
          : lang === "md"
            ? mdTokens(text)
            : [{ text }];
  const lines: Token[][] = [[]];
  for (const t of tokens) {
    const parts = t.text.split("\n");
    parts.forEach((part, k) => {
      if (k > 0) lines.push([]);
      if (part) lines[lines.length - 1].push({ text: part, kind: t.kind });
    });
  }
  // A trailing newline doesn't make an extra empty line.
  if (lines.length > 1 && lines[lines.length - 1].length === 0 && text.endsWith("\n")) lines.pop();
  return lines;
}
